import React, { useState, useEffect } from 'react';
import { Search, Plus, X, Loader2, Edit2, Trash2, Bell, CheckCircle2, XCircle, Megaphone } from 'lucide-react';

export default function NoticeManagement() {
  const [noticesList, setNoticesList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [tableLoading, setTableLoading] = useState(false);

  // Pagination Parameters
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Modal Controls
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Form State tailored strictly to dashboard_notices table
  const [noticeForm, setNoticeForm] = useState({
    title: '',
    message: '',
    is_active: 1
  });

  useEffect(() => {
    fetchNotices();
  }, [searchTerm]);

  // 1. GET - Fetch notices
  const fetchNotices = async () => {
    setTableLoading(true);
    try {
      const res = await fetch(`https://metapi.emedhatechnologies.com/api/notices?search=${encodeURIComponent(searchTerm)}`);
      const result = await res.json();
      if (result.success) {
        setNoticesList(result.data || []);
      } else {
        setNoticesList([]);
      }
    } catch (err) {
      console.error("Failed to fetch notices:", err);
      setNoticesList([]);
    } finally {
      setTableLoading(false);
    }
  };

  const handleOpenAddModal = () => {
    setEditingId(null);
    setNoticeForm({
      title: '',
      message: '',
      is_active: 1
    });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (item) => {
    setEditingId(item.id);
    setNoticeForm({
      title: item.title,
      message: item.message,
      is_active: item.is_active !== undefined ? Number(item.is_active) : 1
    });
    setIsModalOpen(true);
  };

  // 2. POST & PUT - Create or update notice
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const endpoint = editingId 
      ? `https://metapi.emedhatechnologies.com/api/notices/update/${editingId}` 
      : 'https://metapi.emedhatechnologies.com/api/notices/add';
    const method = editingId ? 'PUT' : 'POST';

    try {
      const res = await fetch(endpoint, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(noticeForm)
      });
      const result = await res.json();

      if (result.success) {
        setIsModalOpen(false);
        fetchNotices();
      } else {
        alert(result.message || "Operation failed.");
      }
    } catch (err) {
      console.error("Failed to save notice:", err);
      alert("Network error occurred while saving the notice.");
    }
  };

  // 3. DELETE - Remove notice
  const handleDeleteEntry = async (id) => {
    if (!window.confirm("Are you sure you want to remove this announcement notice?")) return;
    try {
      const res = await fetch(`https://metapi.emedhatechnologies.com/api/notices/delete/${id}`, {
        method: 'DELETE'
      });
      const result = await res.json();
      if (result.success) {
        fetchNotices();
      } else {
        alert(result.message || "Failed to delete notice.");
      }
    } catch (err) {
      console.error("Failed to delete notice:", err);
    }
  };

  // 4. Quick Toggle for is_active
  const handleToggleStatus = async (item) => {
    const newStatus = Number(item.is_active) === 1 ? 0 : 1;
    try {
      const res = await fetch(`https://metapi.emedhatechnologies.com/api/notices/toggle-status/${item.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: newStatus })
      });
      const result = await res.json();
      if (result.success) {
        fetchNotices();
      }
    } catch (err) {
      console.error("Failed to toggle status:", err);
    }
  };

  // Pagination Computations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = noticesList.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(noticesList.length / itemsPerPage);

  return (
    <div style={styles.container}>
      
      {/* TOP HEADER CTA BAR */}
      <div style={styles.topActionBar}>
        <div>
          <h2 style={styles.mainTitle}>Dashboard Notices & Announcements</h2>
          <p style={styles.mainSubtitle}>Publish and manage real-time notices displayed on student and administrative portals</p>
        </div>
        <button onClick={handleOpenAddModal} style={styles.addBtn}>
          <Plus size={15} style={{ marginRight: '6px' }} /> Publish New Notice
        </button>
      </div>

      {/* SEARCH STRIP */}
      <div style={styles.filterControlPanel}>
        <div style={styles.searchWrapper}>
          <Search size={16} style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search notices by title or message content..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            style={styles.tableSearchInput}
          />
        </div>
        <div style={styles.totalIndicator}>
          Total Announcements: <strong style={{ color: '#1c6ec9' }}>{noticesList.length}</strong>
        </div>
      </div>

      {/* MASTER DATA TABLE */}
      {tableLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} color="#1c6ec9" />
        </div>
      ) : (
        <div style={{ overflowX: 'auto', width: '100%' }}>
          <table style={styles.dataTable}>
            <thead>
              <tr>
                <th style={{ ...styles.tableTh, width: '60px' }}>ID</th>
                <th style={{ ...styles.tableTh, width: '220px' }}>Notice Title</th>
                <th style={styles.tableTh}>Message / Description</th>
                <th style={{ ...styles.tableTh, width: '120px' }}>Status</th>
                <th style={{ ...styles.tableTh, width: '160px' }}>Posted / Updated</th>
                <th style={{ ...styles.tableTh, width: '150px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {noticesList.length === 0 ? (
                <tr><td colSpan="6" style={styles.noDataTd}>No dashboard notices found.</td></tr>
              ) : (
                currentItems.map((item) => (
                  <tr key={item.id} style={styles.tableTr}>
                    <td style={{ ...styles.tableTd, fontWeight: '700', color: '#0f172a' }}>
                      #{item.id}
                    </td>
                    <td style={styles.tableTd}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={styles.iconThumb}>
                          <Megaphone size={15} color="#1c6ec9" />
                        </div>
                        <strong style={{ color: '#0f172a', fontSize: '13.5px' }}>{item.title}</strong>
                      </div>
                    </td>
                    <td style={styles.tableTd}>
                      <div style={styles.messageBox} title={item.message}>
                        {item.message}
                      </div>
                    </td>
                    <td style={styles.tableTd}>
                      <button 
                        onClick={() => handleToggleStatus(item)}
                        style={Number(item.is_active) === 1 ? styles.statusActiveBtn : styles.statusInactiveBtn}
                        title="Click to toggle display status"
                      >
                        {Number(item.is_active) === 1 ? (
                          <><CheckCircle2 size={12} /> Active</>
                        ) : (
                          <><XCircle size={12} /> Hidden</>
                        )}
                      </button>
                    </td>
                    <td style={styles.tableTd}>
                      <div style={{ fontSize: '12px', color: '#334155' }}>
                        {item.updated_at ? new Date(item.updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                      </div>
                      <span style={{ fontSize: '11px', color: '#64748b' }}>
                        {item.updated_at ? new Date(item.updated_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </td>
                    <td style={styles.tableTd}>
                      <div style={styles.actionBtnGroup}>
                        <button onClick={() => handleOpenEditModal(item)} style={styles.editRowBtn} title="Edit Notice">
                          <Edit2 size={12} /> Edit
                        </button>
                        <button onClick={() => handleDeleteEntry(item.id)} style={styles.deleteRowBtn} title="Delete Notice">
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* PAGINATION TOOLBAR */}
      {noticesList.length > 0 && (
        <div style={styles.paginationDock}>
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            style={currentPage === 1 ? { ...styles.pageBtn, ...styles.pageBtnDisabled } : styles.pageBtn}
          >
            Previous
          </button>
          <span style={styles.pageIndicator}>
            Page <strong>{currentPage}</strong> of {totalPages || 1}
          </span>
          <button
            disabled={currentPage === totalPages || totalPages === 0}
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            style={currentPage === totalPages || totalPages === 0 ? { ...styles.pageBtn, ...styles.pageBtnDisabled } : styles.pageBtn}
          >
            Next
          </button>
        </div>
      )}

      {/* MODAL POPUP WINDOW */}
      {isModalOpen && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modalWrapperCard}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Bell size={16} color="#1c6ec9" />
                {editingId ? 'Edit Announcement Notice' : 'Publish New Announcement'}
              </h3>
              <button onClick={() => setIsModalOpen(false)} style={styles.modalCloseX}><X size={18} /></button>
            </div>

            <form onSubmit={handleFormSubmit} style={styles.modalForm}>
              
              {/* TITLE & STATUS ROW */}
              <div style={styles.formSplitGrid}>
                <div style={styles.fieldContainer}>
                  <label style={styles.fieldLabel}>Notice Title *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Notice & Updates"
                    value={noticeForm.title}
                    onChange={(e) => setNoticeForm({ ...noticeForm, title: e.target.value })}
                    style={styles.formInput}
                  />
                </div>

                <div style={styles.fieldContainer}>
                  <label style={styles.fieldLabel}>Publication Status *</label>
                  <select
                    value={noticeForm.is_active}
                    onChange={(e) => setNoticeForm({ ...noticeForm, is_active: Number(e.target.value) })}
                    style={styles.formSelect}
                  >
                    <option value={1}>Active (Visible on App)</option>
                    <option value={0}>Inactive (Draft / Hidden)</option>
                  </select>
                </div>
              </div>

              {/* MESSAGE TEXTAREA */}
              <div style={styles.fieldContainer}>
                <label style={styles.fieldLabel}>Notice Content / Body Message *</label>
                <textarea
                  rows={4}
                  required
                  placeholder="Enter detailed notice or alert message here..."
                  value={noticeForm.message}
                  onChange={(e) => setNoticeForm({ ...noticeForm, message: e.target.value })}
                  style={styles.formTextArea}
                />
              </div>

              <div style={styles.formActionDock}>
                <button type="button" onClick={() => setIsModalOpen(false)} style={styles.formCancelBtn}>Cancel</button>
                <button type="submit" style={styles.formSaveBtn}>
                  {editingId ? 'Save Changes' : 'Publish Announcement'}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

    </div>
  );
}

// FORMATTING STYLES MATRIX
const styles = {
  container: { width: '100%', boxSizing: 'border-box' },
  topActionBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' },
  mainTitle: { margin: 0, fontSize: '20px', fontWeight: '800', color: '#0f172a' },
  mainSubtitle: { margin: '2px 0 0 0', fontSize: '13px', color: '#64748b' },
  addBtn: { background: 'linear-gradient(90deg, #e30504 0%, #a80302 100%)', border: 'none', borderRadius: '12px', color: '#fff', padding: '12px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' },
  filterControlPanel: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '16px', flexWrap: 'wrap', borderBottom: '1px solid #edf2f7', paddingBottom: '14px' },
  searchWrapper: { position: 'relative', display: 'flex', alignItems: 'center', flex: 1, maxWidth: '380px', width: '100%' },
  searchIcon: { position: 'absolute', left: '12px', color: '#94a3b8' },
  tableSearchInput: { width: '100%', padding: '9px 12px 9px 36px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '9px', color: '#0f172a', fontSize: '13.5px', outline: 'none' },
  totalIndicator: { fontSize: '14.5px', fontWeight: '600', color: '#334155' },
  dataTable: { width: '100%', borderCollapse: 'collapse', textAlign: 'left' },
  tableTh: { padding: '12px 14px', borderBottom: '2px solid #e2e8f0', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', whiteSpace: 'nowrap' },
  tableTr: { borderBottom: '1px solid #f1f5f9', background: '#ffffff' },
  tableTd: { padding: '12px 14px', color: '#475569', fontSize: '13px', verticalAlign: 'middle' },
  noDataTd: { padding: '30px 14px', textAlign: 'center', color: '#94a3b8', fontSize: '14px', fontWeight: '500' },
  iconThumb: { width: '32px', height: '32px', borderRadius: '8px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  messageBox: { maxWidth: '420px', whiteSpace: 'normal', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '1.5', color: '#334155' },
  statusActiveBtn: { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', padding: '4px 8px', borderRadius: '6px', fontSize: '11.5px', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' },
  statusInactiveBtn: { background: '#f8fafc', border: '1px solid #cbd5e1', color: '#64748b', padding: '4px 8px', borderRadius: '6px', fontSize: '11.5px', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' },
  actionBtnGroup: { display: 'flex', gap: '6px', alignItems: 'center' },
  editRowBtn: { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', padding: '5px 10px', borderRadius: '6px', fontSize: '11.5px', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '3px' },
  deleteRowBtn: { background: '#fff1f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '5px 10px', borderRadius: '6px', fontSize: '11.5px', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '3px' },
  modalBackdrop: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.25)', backdropFilter: 'blur(8px)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  modalWrapperCard: { background: '#ffffff', border: '1px solid rgba(255,255,255,0.8)', borderRadius: '20px', width: '100%', maxWidth: '560px', maxHeight: '90vh', padding: '24px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.05)', overflowY: 'auto' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' },
  modalCloseX: { background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' },
  modalForm: { display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '14px' },
  formSplitGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  fieldContainer: { display: 'flex', flexDirection: 'column', gap: '4px' },
  fieldLabel: { color: '#64748b', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' },
  formInput: { width: '100%', padding: '9px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#0f172a', fontSize: '13px', outline: 'none', boxSizing: 'border-box' },
  formSelect: { padding: '9px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#0f172a', fontSize: '13px', outline: 'none' },
  formTextArea: { width: '100%', padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#0f172a', fontSize: '13px', outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' },
  formActionDock: { display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px', borderTop: '1px solid #e2e8f0', paddingTop: '12px' },
  formCancelBtn: { background: 'transparent', border: '1px solid #e2e8f0', color: '#64748b', padding: '9px 16px', borderRadius: '8px', cursor: 'pointer' },
  formSaveBtn: { background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)', border: 'none', color: '#fff', padding: '9px 16px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' },
  paginationDock: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #edf2f7' },
  pageIndicator: { fontSize: '13px', color: '#475569' },
  pageBtn: { background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px 14px', color: '#334155', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer' },
  pageBtnDisabled: { background: '#f8fafc', color: '#94a3b8', cursor: 'not-allowed', borderColor: '#e2e8f0' }
};